"use client";

import { useState, useEffect } from "react";
import { usePathname } from "next/navigation";

interface AvatarWidgetProps {
  channelId: string;
  avatarImage: string;
  avatarName?: string;
  greeting?: string;
  ringColor?: string;
  position?: "bottom-right" | "bottom-left";
  baseUrl?: string;
  allowedPaths?: string[];
}

function normalizePath(path: string) {
  const withSlash = path.startsWith("/") ? path : `/${path}`;
  return withSlash.length > 1 ? withSlash.replace(/\/+$/, "") : withSlash;
}

/** Space reserved for UserWay at the viewport bottom (widget + gap). */
const USERWAY_GAP = 25;
const USERWAY_SIZE = 44;
const USERWAY_VIEWPORT_BOTTOM = 20;
const USERWAY_CLEARANCE =
  USERWAY_VIEWPORT_BOTTOM + USERWAY_SIZE + USERWAY_GAP;
const MOBILE_TOP_INSET = 16;

export function AvatarWidget({
  channelId,
  avatarImage,
  avatarName = "AI Assistant",
  greeting = "Hi! Click here to start a conversation.",
  ringColor = "#E87A2E",
  position = "bottom-right",
  baseUrl = "https://avatar.sumvivas.com",
  allowedPaths,
}: AvatarWidgetProps) {
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [showGreeting, setShowGreeting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const isAllowed =
    !allowedPaths ||
    allowedPaths.some(
      (path) => normalizePath(path) === normalizePath(pathname ?? "/"),
    );

  useEffect(() => {
    if (!isAllowed) {
      setIsOpen(false);
      return;
    }

    const timer = setTimeout(() => setShowGreeting(true), 1000);
    return () => clearTimeout(timer);
  }, [isAllowed]);

  if (!isAllowed) return null;

  const bottomOffset = 100;
  const openBottomOffset = Math.max(bottomOffset, USERWAY_CLEARANCE);
  const posStyle = position === "bottom-left" ? { left: 24 } : { right: 24 };

  return (
    <>
      {!isOpen && (
        <div
          onClick={() => setIsOpen(true)}
          style={{
            position: "fixed",
            bottom: bottomOffset,
            ...posStyle,
            width: 64,
            height: 64,
            borderRadius: "50%",
            border: `3px solid ${ringColor}`,
            cursor: "pointer",
            zIndex: 99999,
            overflow: "hidden",
            background: "#fff",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
          }}
        >
          <img
            src={avatarImage}
            alt={avatarName}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              borderRadius: "50%",
              padding: 10,
              boxSizing: "border-box",
            }}
          />
        </div>
      )}

      {!isOpen && showGreeting && (
        <div
          onClick={() => setIsOpen(true)}
          style={{
            position: "fixed",
            bottom: bottomOffset + 14,
            ...(position === "bottom-left" ? { left: 100 } : { right: 100 }),
            background: "#1a1a2e",
            color: "#fff",
            padding: "10px 16px",
            borderRadius: 20,
            fontSize: 14,
            zIndex: 99998,
            cursor: "pointer",
            boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
          }}
        >
          {greeting}
        </div>
      )}

      {isOpen && (
        <div
          style={{
            position: "fixed",
            ...(isMobile
              ? {
                  top: MOBILE_TOP_INSET,
                  bottom: USERWAY_CLEARANCE,
                  left: "50%",
                  right: "auto",
                  transform: "translateX(-50%)",
                  width: "90%",
                  maxWidth: 420,
                  height: "auto",
                }
              : {
                  bottom: openBottomOffset,
                  ...posStyle,
                  width: 420,
                  height: 680,
                  maxHeight: `calc(100vh - ${openBottomOffset + 30}px)`,
                }),
            borderRadius: 16,
            overflow: "hidden",
            zIndex: 100000,
            boxShadow: "0 12px 48px rgba(0,0,0,0.5)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            aria-label="Close chat"
            style={{
              position: "absolute",
              top: 12,
              left: 12,
              width: 32,
              height: 32,
              borderRadius: "50%",
              background: "rgba(0,0,0,0.6)",
              color: "#fff",
              border: "none",
              fontSize: 18,
              cursor: "pointer",
              zIndex: 100001,
            }}
          >
            &times;
          </button>
          <iframe
            src={`${baseUrl}/?url=${channelId}`}
            title={`${avatarName} AI Assistant`}
            allow="microphone; clipboard-read; clipboard-write;"
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              borderRadius: 16,
            }}
          />
        </div>
      )}
    </>
  );
}
